import * as React from "react";
import { ChevronRightIcon, FileIcon, FolderIcon } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// ============================================================================
// Context Types
// ============================================================================

type TreeItemData = {
  id: string;
  name: string;
  type?: "file" | "folder";
  [key: string]: unknown;
};

type TreeContextValue = {
  selected?: string;
  onSelect?: (id: string, item: TreeItemData) => void;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
};

type TreeItemContextValue = {
  id: string;
  value: TreeItemData;
  isExpanded: boolean;
  hasChildren: boolean;
  level: number;
  isSelected: boolean;
};

// ============================================================================
// Contexts
// ============================================================================

const TreeContext = React.createContext<TreeContextValue | undefined>(
  undefined,
);

const TreeItemContext = React.createContext<TreeItemContextValue | undefined>(
  undefined,
);

// ============================================================================
// Hooks
// ============================================================================

function useTree() {
  const context = React.useContext(TreeContext);
  if (!context) {
    throw new Error("useTree must be used within a Tree");
  }
  return context;
}

function useTreeItem() {
  const context = React.useContext(TreeItemContext);
  if (!context) {
    throw new Error("useTreeItem must be used within a TreeItem");
  }
  return context;
}

// ============================================================================
// Tree (Root)
// ============================================================================

interface TreeProps extends Omit<React.ComponentProps<"div">, "onSelect"> {
  selected?: string;
  onSelect?: (id: string, item: TreeItemData) => void;
  defaultExpanded?: string[];
  expanded?: string[];
  onExpandedChange?: (expanded: string[]) => void;
}

function Tree({
  children,
  selected,
  onSelect,
  defaultExpanded = [],
  expanded: controlledExpanded,
  onExpandedChange,
  className,
  ...props
}: TreeProps) {
  const [internalExpanded, setInternalExpanded] = React.useState<Set<string>>(
    () => new Set(defaultExpanded),
  );

  const expanded = React.useMemo(
    () =>
      controlledExpanded !== undefined
        ? new Set(controlledExpanded)
        : internalExpanded,
    [controlledExpanded, internalExpanded],
  );

  const toggleExpanded = React.useCallback(
    (id: string) => {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }

      if (controlledExpanded === undefined) {
        setInternalExpanded(newExpanded);
      }
      onExpandedChange?.(Array.from(newExpanded));
    },
    [expanded, controlledExpanded, onExpandedChange],
  );

  const contextValue = React.useMemo<TreeContextValue>(
    () => ({
      selected,
      onSelect,
      expanded,
      toggleExpanded,
    }),
    [selected, onSelect, expanded, toggleExpanded],
  );

  return (
    <TreeContext.Provider value={contextValue}>
      <div
        data-slot="tree"
        role="tree"
        className={cn("flex flex-col", className)}
        {...props}
      >
        {children}
      </div>
    </TreeContext.Provider>
  );
}

// ============================================================================
// TreeItem
// ============================================================================

interface TreeItemProps extends React.ComponentProps<"div"> {
  id: string;
  value: TreeItemData;
}

const TreeItemLevelContext = React.createContext<number>(0);

function TreeItem({ children, id, value, className, ...props }: TreeItemProps) {
  const { selected, expanded } = useTree();
  const parentLevel = React.useContext(TreeItemLevelContext);
  const level = parentLevel;

  const hasChildren = React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) &&
      ((child.type as React.ComponentType)?.displayName === "TreeItemContent" ||
        child.type === TreeItemContent),
  );

  const isExpanded = expanded.has(id);
  const isSelected = selected === id;

  const contextValue = React.useMemo<TreeItemContextValue>(
    () => ({
      id,
      value,
      isExpanded,
      hasChildren,
      level,
      isSelected,
    }),
    [id, value, isExpanded, hasChildren, level, isSelected],
  );

  return (
    <TreeItemContext.Provider value={contextValue}>
      <TreeItemLevelContext.Provider value={level + 1}>
        <div
          data-slot="tree-item"
          role="treeitem"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isSelected}
          className={cn("relative", className)}
          {...props}
        >
          {children}
        </div>
      </TreeItemLevelContext.Provider>
    </TreeItemContext.Provider>
  );
}

// ============================================================================
// TreeItemTrigger
// ============================================================================

type TreeItemTriggerProps = Omit<
  React.ComponentProps<"div">,
  "onClick"
>

function TreeItemTrigger({
  children,
  className,
  ...props
}: TreeItemTriggerProps) {
  const { toggleExpanded, onSelect } = useTree();
  const { id, value, level, isSelected, hasChildren } = useTreeItem();

  const handleClick = () => {
    if (hasChildren) {
      toggleExpanded(id);
    }
    onSelect?.(id, value);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-slot="tree-item-trigger"
      data-selected={isSelected}
      data-level={level}
      className={cn(
        "flex h-8 w-full min-w-0 cursor-pointer select-none items-center justify-start gap-2 rounded-sm pr-2 text-sm font-medium",
        "hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        isSelected && "bg-accent",
        className,
      )}
      style={
        {
          paddingLeft: `${level * 24 + 8}px`,
          "--tree-level": level,
        } as React.CSSProperties
      }
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TreeItemIndicator
// ============================================================================

type TreeItemIndicatorProps = React.ComponentProps<"span">

function TreeItemIndicator({ className, ...props }: TreeItemIndicatorProps) {
  const { isExpanded, hasChildren } = useTreeItem();

  if (!hasChildren) {
    return <span className="size-4 shrink-0" {...props} />;
  }

  return (
    <span
      data-slot="tree-item-indicator"
      className={cn("size-4 shrink-0 transition-transform", className)}
      {...props}
    >
      <ChevronRightIcon
        className={cn(
          "size-4 transition-transform duration-200",
          isExpanded && "rotate-90",
        )}
      />
    </span>
  );
}

// ============================================================================
// TreeItemIcon
// ============================================================================

interface TreeItemIconProps extends React.ComponentProps<"span"> {
  fileIcon?: React.ReactNode;
  folderIcon?: React.ReactNode;
}

function TreeItemIcon({
  className,
  fileIcon,
  folderIcon,
  children,
  ...props
}: TreeItemIconProps) {
  const { value, hasChildren } = useTreeItem();

  // If children are provided, render them directly
  if (children) {
    return (
      <span
        data-slot="tree-item-icon"
        className={cn("size-4 shrink-0", className)}
        {...props}
      >
        {children}
      </span>
    );
  }

  const isFolder = value.type === "folder" || hasChildren;
  const defaultIcon = isFolder ? (
    <FolderIcon className="size-4 text-muted-foreground" />
  ) : (
    <FileIcon className="size-4 text-muted-foreground" />
  );

  const icon = isFolder ? folderIcon || defaultIcon : fileIcon || defaultIcon;

  return (
    <span
      data-slot="tree-item-icon"
      className={cn("size-4 shrink-0", className)}
      {...props}
    >
      {icon}
    </span>
  );
}

// ============================================================================
// TreeItemLabel
// ============================================================================

type TreeItemLabelProps = React.ComponentProps<"span">

function TreeItemLabel({ className, ...props }: TreeItemLabelProps) {
  return (
    <span
      data-slot="tree-item-label"
      className={cn("truncate", className)}
      {...props}
    />
  );
}

// ============================================================================
// TreeItemContent
// ============================================================================

type TreeItemContentProps = React.ComponentProps<"div">

function TreeItemContent({
  children,
  className,
  ...props
}: TreeItemContentProps) {
  const { isExpanded, id } = useTreeItem();
  const { toggleExpanded } = useTree();

  return (
    <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(id)}>
      <CollapsibleContent
        data-slot="tree-item-content"
        role="group"
        className={cn("flex flex-col", className)}
        {...props}
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

TreeItemContent.displayName = "TreeItemContent";

// ============================================================================
// Exports
// ============================================================================

/* eslint-disable react-refresh/only-export-components -- hooks are co-located with their tree components */
export {
  Tree,
  TreeItem,
  TreeItemTrigger,
  TreeItemIndicator,
  TreeItemIcon,
  TreeItemLabel,
  TreeItemContent,
  useTree,
  useTreeItem,
};
/* eslint-enable react-refresh/only-export-components */

export type { TreeProps, TreeItemProps, TreeItemData };
